import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button, Card, Icon, Tag } from '@repo/design-system';
import type { IconName, TagTone } from '@repo/design-system';
import { fontMono, ink, space, typeRole } from '@repo/design-tokens';
import { IconDisc } from '@repo/ui';
import type { IconDiscTone } from '@repo/ui';

/**
 * The summary's card and its read rows (`twin-details-page-summary`, R2; frame 03). An app
 * composition of design-system parts, as the epic records (*the summary cards*): `Card` on glass —
 * the glass card itself (`on-scene`) where it floats over the washed step, as Twin details' four do,
 * else the inlay (`on-glass`, the default) inside a glass panel (`onboarding-persistent-scene-wash-panels-glass`, R2.1) —
 * an `IconDisc`, the title, and *Edit* / *Done* at the top right; label–value rows with a tag at the
 * right. `OverrideRows` is not used: its source line and single text action are not frame 03's rows.
 *
 * **One button, two states.** *Edit* and *Done* are the same element, so focus stays on it when it
 * flips. It comes LAST in the DOM — after the card's fields — and is placed at the top right by the
 * grid, so the keyboard reaches a card's fields before its *Done* (spec criterion 11). A card that
 * never edits in place (`actionFirst`) puts it first, so its *Edit* comes before the body's own
 * controls. Opening a card with its *Edit* moves focus to its first field; a card opened any other
 * way (a refused rename coming back) leaves focus where the person is.
 */

export interface SummaryRow {
  key: string;
  label: string;
  value: React.ReactNode;
  /** Codes and coordinates read in mono (R2.2). */
  mono?: boolean;
  /** Free text whose line breaks are kept (a twin's description). */
  multiline?: boolean;
  tag?: { tone: TagTone; text: string; lock?: boolean; label?: string };
}

export const SummaryRows: React.FC<{ rows: SummaryRow[] }> = ({ rows }) => (
  <dl className="summary-rows">
    {rows.map(row => (
      <div key={row.key} className="summary-row">
        <dt>{row.label}</dt>
        <dd
          className={
            [row.mono && 'mono', row.multiline && 'multiline'].filter(Boolean).join(' ') ||
            undefined
          }
        >
          {row.value}
        </dd>
        {row.tag && (
          <Tag
            surface="on-glass"
            size="dense"
            tone={row.tag.tone}
            className="row-tag"
            aria-label={row.tag.label}
            icon={row.tag.lock ? <Icon name="lock" variant="solid" size="xs" /> : undefined}
          >
            {row.tag.text}
          </Tag>
        )}
      </div>
    ))}
  </dl>
);

interface Props {
  className?: string;
  title: string;
  /** The disc's glyph; none, and no disc, when there is nothing true to show (R3.1's fallback). */
  glyph?: IconName;
  discTone: IconDiscTone;
  /** In edit: *Done* in place of *Edit*, and the selected border (R2.3). */
  editing?: boolean;
  /** *Edit* pressed. */
  onEdit: () => void;
  /** *Done* pressed. */
  onDone?: () => void;
  /** The button before the body in the DOM: for a card whose body holds controls and no edit. */
  actionFirst?: boolean;
  /** `on-scene` over the step's wash; `on-glass` (the default) inside a glass panel. */
  surface?: 'on-scene' | 'on-glass';
  children: React.ReactNode;
}

const FIELD_SELECTOR = 'input:not([type="hidden"]), textarea, [role="combobox"]';

const SummaryCard: React.FC<Props> = ({
  className,
  title,
  glyph,
  discTone,
  editing = false,
  onEdit,
  onDone,
  actionFirst = false,
  surface = 'on-glass',
  children,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  /** Set by *Edit*: only an opening the person asked for takes their focus. */
  const openedByEdit = useRef(false);
  useEffect(() => {
    if (editing && openedByEdit.current) {
      bodyRef.current?.querySelector<HTMLElement>(FIELD_SELECTOR)?.focus();
    }
    openedByEdit.current = false;
  }, [editing]);

  const action = (
    <div className="card-action">
      <Button
        variant={editing ? 'primary' : 'frosted'}
        size="sm"
        iconLeft={<Icon name={editing ? 'check' : 'pen'} size="xs" />}
        aria-label={editing ? `Done editing ${title}` : `Edit ${title}`}
        onClick={
          editing
            ? onDone
            : () => {
                openedByEdit.current = true;
                onEdit();
              }
        }
      >
        {editing ? 'Done' : 'Edit'}
      </Button>
    </div>
  );

  return (
    <Card
      surface={surface}
      size="md"
      selected={editing}
      className={`${className ?? ''} summary-card`}
    >
      <div className="card-head">
        {glyph && (
          <IconDisc tone={discTone} size={30}>
            <Icon name={glyph} variant="solid" size="xs" />
          </IconDisc>
        )}
        <h3>{title}</h3>
      </div>
      {actionFirst && action}
      <div className="card-body" ref={bodyRef}>
        {children}
      </div>
      {!actionFirst && action}
    </Card>
  );
};

export default styled(SummaryCard)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'head action'
    'body body';
  gap: ${space[3]};
  align-content: start;

  .card-head {
    grid-area: head;
    display: flex;
    align-items: center;
    gap: ${space[3]};
    min-width: 0;
  }

  .card-head h3 {
    margin: 0;
    font: ${typeRole.body1Stronger.fontWeight} ${typeRole.body1Stronger.fontSize} /
      ${typeRole.body1Stronger.lineHeight} ${typeRole.body1Stronger.fontFamily};
    color: ${ink[1]};
  }

  .card-action {
    grid-area: action;
    align-self: center;
  }

  .card-body {
    grid-area: body;
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
    min-width: 0;
  }

  .summary-rows {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
  }

  .summary-row {
    display: grid;
    grid-template-columns: minmax(96px, auto) minmax(0, 1fr) auto;
    align-items: center;
    gap: ${space[3]};
  }

  .summary-row dt {
    font: ${typeRole.caption1.fontWeight} ${typeRole.caption1.fontSize} /
      ${typeRole.caption1.lineHeight} ${typeRole.caption1.fontFamily};
    color: ${ink[3]};
  }

  .summary-row dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
    font: ${typeRole.body1.fontWeight} ${typeRole.body1.fontSize} / ${typeRole.body1.lineHeight}
      ${typeRole.body1.fontFamily};
    color: ${ink[1]};
  }

  .summary-row dd.mono {
    font-family: ${fontMono};
  }

  .summary-row dd.multiline {
    white-space: pre-line;
  }

  .reason {
    margin: 0;
    font: ${typeRole.caption1.fontWeight} ${typeRole.caption1.fontSize} /
      ${typeRole.caption1.lineHeight} ${typeRole.caption1.fontFamily};
    color: ${ink[3]};
  }
`;
